<template>
  <div class="flex flex-col items-center">
    <AlertBanner v-if="hasPhotoRejected" id="submit-error-alert" :status="0" class="mt-0 sm:mt-32 lg:mt-0" style="width: 100%">
      <div>
        Your profile photo did not meet our
        <a
          target="_blank"
          href="https://support.sittercity.com/hc/en-us/articles/115012435268-Profile-photo-guidelines-for-sitters"
          class="primary-link"
          >Photo Guidelines</a
        >. Check the guidelines and <a href="/sitter/photo_upload#/sitter_upload" class="primary-link">upload a new photo</a>.
      </div>
    </AlertBanner>
    <AlertBanner
      v-if="showPhotoUploadAlert"
      id="photo-upload-alert"
      :status="1"
      class="mt-0 sm:mt-32 lg:mt-0"
      style="width: 100%"
    >
      <div>Your photo is saved and will be reviewed by our team shortly.</div>
    </AlertBanner>
    <AlertBanner v-if="hasExternalReviewSitterError" id="external-reviews-sitter-error" :status="3" style="width: 100%">
      <div>Only parents can leave a review</div>
    </AlertBanner>
    <div class="max-w-736 px-16 relative">
      <ProfileEdit :account="account" :profile-account-data="profileAccountData" />
      <ResourceCards class="mt-32" />
      <hr class="border-grey-divider-subdued my-32" />
      <spinner v-if="trustedCaregiverLoading" class="m-auto mt-64" :width="48" :height="48" />
      <TrustedCaregiverProgress
        v-else-if="showTrustedCaregiverProgress"
        :trusted-caregiver="trustedCaregiver"
        :care-type="trustedCaregiverCareType"
      />
      <ProfileProgress v-else />
      <WorkLikeAPro
        :sitter-id="account.id"
        :sitter-name="`${account.firstName} ${account.lastName ? account.lastName[0] : ''}.`"
        :initial-show-review-modal="showReviewModal"
      />
    </div>
    <InfoJustForYou />
  </div>
</template>

<script lang="ts">
import { mapGetters } from 'vuex';
import { sendBrazeEvent } from '../../../lib/braze';
import ProfileEdit from './profile/ProfileEdit.vue';
import ResourceCards from './ResourceCards.vue';
import WorkLikeAPro from './WorkLikeAPro.vue';
import InfoJustForYou from './InfoJustForYou.vue';
import ProfileProgress from './profile_progress/ProfileProgress.vue';
import TrustedCaregiverProgress from './trusted_caregiver/TrustedCaregiverProgress.vue';
import {
  mapTrustedCaregiverFromApi,
  TRUSTED_CAREGIVER_CHECKLIST_FLAG,
  type TrustedCaregiver,
} from './trusted_caregiver/trustedCaregiverProgressCopy';
import { ACCOUNT_PROFILES_QUERY } from '@/graphql/account/provider/queries';
import { FETCH_TRUSTED_CAREGIVER } from '@/graphql/provider/trusted_caregiver/queries';
import { SITTER_DASHBOARD_LOADED, SHOW_SITTER_SMS_MODAL } from '@/lib/constants/braze_custom_events';
import AlertBanner from '@/components/alert_banner/AlertBanner.vue';
import Spinner from '@/components/spinner/Spinner.vue';
import { readDomainCookie, setPhotoRejectCookie, deleteDomainCookie } from '@/lib/domain_cookie';
import { refreshStalePhotoData } from '@/helpers/account/refreshStalePhotoData';
import { shouldShowSMSModal } from '../../../lib/page';
import { isOn } from '@/lib/growthbook';

export default {
  name: 'SitterDashboard',
  components: {
    ProfileEdit,
    ResourceCards,
    InfoJustForYou,
    WorkLikeAPro,
    AlertBanner,
    ProfileProgress,
    TrustedCaregiverProgress,
    Spinner,
  },

  data() {
    const showPhotoUploadAlert = readDomainCookie('profilePhotoAlertSuccess') === 'true';
    deleteDomainCookie('profilePhotoAlertSuccess');

    return {
      hasPhotoRejected: false,
      showPhotoUploadAlert,
      timeout: null as ReturnType<typeof setTimeout> | null,
      profileAccountData: {} as Record<string, unknown>,
      accountProfilesLoaded: false,
      trustedCaregiverQueryLoading: 0,
      trustedCaregiver: null as TrustedCaregiver | null,
    };
  },
  computed: {
    ...mapGetters('account', {
      account: 'getAccount',
    }),
    isTrustedCaregiverChecklistEnabled(): boolean {
      return isOn(TRUSTED_CAREGIVER_CHECKLIST_FLAG);
    },
    hasBabysitterOrNannyProfile(): boolean {
      const profiles = (this.profileAccountData as any)?.profiles;
      return Boolean(profiles?.babysitter?.profileCompleted || profiles?.nanny?.profileCompleted);
    },
    trustedCaregiverCareType(): string {
      const profiles = (this.profileAccountData as any)?.profiles;
      if (profiles?.babysitter?.profileCompleted) return 'babysitter';
      if (profiles?.nanny?.profileCompleted) return 'nanny';
      return 'babysitter';
    },
    shouldFetchTrustedCaregiver(): boolean {
      return this.isTrustedCaregiverChecklistEnabled && this.hasBabysitterOrNannyProfile;
    },
    showTrustedCaregiverProgress(): boolean {
      return this.shouldFetchTrustedCaregiver && Boolean(this.trustedCaregiver);
    },
    trustedCaregiverLoading(): boolean {
      if (!this.isTrustedCaregiverChecklistEnabled) return false;
      if (!this.accountProfilesLoaded) return true;
      return this.shouldFetchTrustedCaregiver && this.trustedCaregiverQueryLoading > 0;
    },
    hasExternalReviewSitterError(): boolean {
      const urlParams = new URLSearchParams(window.location.search);
      return !!urlParams.get('ser');
    },
    showReviewModal(): boolean {
      const urlParams = new URLSearchParams(window.location.search);
      const reviewLinkDisplayed = urlParams.get('reviewLinkDisplayed');
      urlParams.delete('reviewLinkDisplayed');

      // Remove the query param from the URL without reloading the page
      const newUrl = urlParams.toString() ? `${window.location.pathname}?${urlParams.toString()}` : window.location.pathname;
      window.history.replaceState(null, '', newUrl);

      return !!reviewLinkDisplayed;
    },
  },

  async mounted() {
    sendBrazeEvent(SITTER_DASHBOARD_LOADED);

    this.profileAccountData = { id: this.account?.id };
    this.fetchAccountProfiles();

    if (this.showPhotoUploadAlert) {
      if (this.account) {
        deleteDomainCookie(`profilePhotoAlert-${this.account.id}`);
        this.hasPhotoRejected = false;
      }

      try {
        await this.$store.dispatch('account/fetchAccountData');
      } catch (error) {
        console.error('Failed to refresh account data on sitter dashboard:', error);
      }
    }

    if (this.shouldShowSMSModal()) {
      sendBrazeEvent(SHOW_SITTER_SMS_MODAL);
    }

    if (this.account) {
      await this.refreshStaleData();

      this.hasPhotoRejected = setPhotoRejectCookie(this.account.photoStatus, `profilePhotoAlert-${this.account.id}`, 'True', 10);
    }

    this.timeout = setTimeout(this.clearAlertBanner, 15000);
  },

  methods: {
    fetchAccountProfiles() {
      if (!this.account?.id) {
        this.accountProfilesLoaded = true;
        return;
      }

      this.$apollo
        .query({
          query: ACCOUNT_PROFILES_QUERY,
          variables: { accountId: this.account.id },
        })
        .then(({ data }) => {
          this.profileAccountData = { ...this.profileAccountData, ...data.account };
        })
        .catch((error) => {
          console.error('Error fetching account profiles:', error);
        })
        .finally(() => {
          this.accountProfilesLoaded = true;
        });
    },
    /**
     * Re-fetch account while a photo is awaiting admin review.
     *
     * Account data is persisted in sessionStorage which skips network fetch when
     * the store already has an account, so a hard refresh or return visit can keep a
     * stale empty avatar even after approval.
     *
     * Refetch if the photo is in an unreviewed state to pick up approval without
     * hitting the network on every dashboard visit.
     */
    async refreshStaleData(): Promise<void> {
      try {
        await refreshStalePhotoData(this.$store, this.account);
      } catch (error) {
        console.error('Failed to refresh account data on sitter dashboard:', error);
      }
    },
    /**
     * Determines if the SMS modal should be shown based on account creation date.
     * Delegates to the shared utility function in lib/page.ts.
     */
    shouldShowSMSModal(): boolean {
      return shouldShowSMSModal(this.account?.createdAt);
    },
    clearAlertBanner() {
      this.hasPhotoRejected = false;
    },
  },
  apollo: {
    trustedCaregiver: {
      query: FETCH_TRUSTED_CAREGIVER,
      loadingKey: 'trustedCaregiverQueryLoading',
      skip() {
        return !this.shouldFetchTrustedCaregiver;
      },
      update(data: { trustedCaregiver?: { checklist: TrustedCaregiver['checklist'] } | null }) {
        return mapTrustedCaregiverFromApi(data?.trustedCaregiver);
      },
      error(err: unknown) {
        this.trustedCaregiver = null;
        console.error(err);
      },
    },
  },
};
</script>

<style lang="scss">
#external-reviews-sitter-error {
  @media only screen and (min-width: 768px) {
    margin-top: -30px;
  }
  @media only screen and (min-width: 1014px) {
    margin-top: 0px;
  }
}
</style>
